import { IPrintServiceInjectable } from '@src/slide/application/service/print-service/core/interface';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import ua from '@tencent/docs-user-agent';
import { isBVisitC } from 'utils';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import i18n from '@i18next/i18n';
import { isEditableDriveFile, isPreviewDriveFile } from '@src/slide/infrastructure/module/dependent/runtime-environment/drive-env';

export const APP_LIMIT_VERSION = '3.5.0';
export const DESKTOP_APP_LIMIT_VERSION = '3.2.3';
export const NEW_PRINT_APP_LIMIT_VERSION = '3.8.0';

export default class PrintServiceInjectable implements IPrintServiceInjectable {
    /**
     * 是否应该离线打印
     */
    public get shouldOfflinePrint(): boolean {
        // 后台接口耗时太久，桌面端都走本地生成； 移动端目前只支持url 维持后台生成pdf
        return browser.isTencentDocDesktopApp;
    }

    /**
     * 是否外部唤起打印界面
     */
    public get shouldInvokeExternalPrint(): boolean {
        return (browser.isTencentDocDesktopApp
            && ua.isElectronTencentDocsClientVersionAboveOrEqual(DESKTOP_APP_LIMIT_VERSION))
            || browser.isTencentDocApp;
    }

    /**
     * 是否调用外部打印，不支持mqq printSupport的老版本
     */
    public get shouldInvokeExternalPrintOnLegacy(): boolean {
        return browser.isTencentDocApp
        && (!ua.isTencentDocsAppVersionAboveOrEqual(APP_LIMIT_VERSION) || ENV.IS_MOBILE_LOCAL_EDIT);
    }

    /**
     * 是否调用app新打印方式: 前端生成pdf给app打印
     */
    public get shouldInvokeNewPrintOnApp() {
        return browser.isTencentDocApp && ua.isTencentDocsAppVersionAboveOrEqual(NEW_PRINT_APP_LIMIT_VERSION);
    }

    /**
     * 是否调用外部(端上)loading ui
     */
    public get shouldInvokeExternalLoading() {
        return browser.isTencentDocApp;
    }

    public get shouldHidePrint() {
        return isPreviewDriveFile();
    }

    public get shouldDisablePrint() {
        return isBVisitC() || !SlideDataServices.PrivilegeManager()?.printable;
    }

    public get pdfNotNeededWhenExternalPrint() {
        // 移动端app不用传入pdf，如果前端生成pdf传入app，会导致app打印进度条一直为0
        // !云文档现在在app上打印必须传入pdf
        return browser.isTencentDocApp && !isEditableDriveFile();
    }

    public invokeExternalPrint(pdf?) {
        const printParam = {
            downloadUrl: pdf,
            printConfig: {
                orientation: 'landscape',
            },
            title: SlideDataServices.SlideTitleManager()?.title,
            type: 4, // file_type: 4 ppt
        };
        window.mqq.invoke('docx', 'airPrint', printParam, null);
    }

    public checkShouldInvokeExternalPrint() {
        if (this.shouldInvokeExternalPrint) {
            return new Promise<boolean>((resolve) => {
                try {
                    window.mqq?.invoke?.('docx', 'printSupport', {}, (result) => {
                        if (result.code === 0) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                } catch (error) {
                    resolve(false);
                }
            });
        }
        return Promise.resolve(false);
    }

    public waitForCancel() {
        return new Promise<boolean>((resolve) => {
            window.mqq?.invoke?.('docx', 'showPrintLoading', {}, (result) => {
                if (result.state === 'cancel') {
                    // 用户取消打印
                    resolve(true);
                }
            });
        });
    }

    public get shouldSendToDeviceToPrint() {
        return browser.isMiniProgram || browser.isWeChat;
    }
}

